import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { useTheme } from '../context/ThemeContext';

const STORAGE_KEY = 'abc_welcome_seen';

function getSeenAddresses(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function markSeen(address: string) {
  try {
    const seen = getSeenAddresses();
    if (!seen.includes(address.toLowerCase())) {
      seen.push(address.toLowerCase());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    }
  } catch {
    // ignore
  }
}

export default function WelcomeModal() {
  const theme = useTheme();
  const { address, isConnected } = useAccount();
  const [open, setOpen] = useState(false);

  const yayPill: React.CSSProperties = {
    padding: '0.125rem 0.375rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: `${theme.success}22`,
    color: theme.success,
  };
  const nayPill: React.CSSProperties = {
    padding: '0.125rem 0.375rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: `${theme.pink}22`,
    color: theme.pink,
  };

  const sectionTitle: React.CSSProperties = {
    fontSize: '0.8125rem',
    fontWeight: 800,
    color: theme.purple ?? theme.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: '1.25rem',
    marginBottom: '0.5rem',
  };

  useEffect(() => {
    if (!isConnected || !address) return;
    const seen = getSeenAddresses();
    const addr = address.toLowerCase();
    if (!seen.includes(addr)) {
      setOpen(true);
    }
  }, [isConnected, address]);

  const handleClose = () => {
    if (address) markSeen(address);
    setOpen(false);
  };

  if (!open) return null;

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
        pointerEvents: 'auto',
      }}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        style={{
          backgroundColor: theme.bgCard ?? 'white',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          maxWidth: '32rem',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 30px 75px rgba(0,0,0,0.6)',
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="welcome-modal-title"
          style={{
            fontSize: theme.headingSize,
            fontWeight: 800,
            marginBottom: '1.25rem',
            color: theme.purple ?? theme.primary,
            textAlign: 'center',
          }}
        >
          Welcome to takni.xyz
        </h3>
        <div style={{ fontSize: '0.875rem', color: theme.textPrimary, lineHeight: 1.7, textAlign: 'justify' }}>
          <p style={{ margin: '0 0 1rem 0' }}>
            Here we decide what we collectively think about the world with a little skin in the game.
          </p>

          <h4 style={{ ...sectionTitle, marginTop: 0 }}>How does it work?</h4>
          <p style={{ margin: '0 0 0.75rem 0' }}>
            Each market = one X post. Stake $1 of ETH and pick either
          </p>
          <ul
            style={{
              margin: '0 0 1rem 0',
              paddingLeft: '1.25rem',
            }}
          >
            <li style={{ marginBottom: '0.35rem' }}>
              <span style={yayPill}>YAY</span> to agree
            </li>
            <li style={{ marginBottom: 0 }}>
              <span style={nayPill}>NAY</span> to disagree
            </li>
          </ul>
          <p style={{ margin: '0 0 1rem 0' }}>
            Live sentiment (% of YAYs vs. NAYs) updates with every vote.
          </p>
          <p style={{ margin: '0 0 1rem 0' }}>
            <span style={yayPill}>YAY</span> 70 / <span style={nayPill}>NAY</span> 30 = 70% of ETH is staked on YAY.
          </p>

          <h4 style={sectionTitle}>How do you get paid?</h4>
          <p style={{ margin: '0 0 0.75rem 0' }}>
            You pick a side and an entry %.
            <br />
            That&apos;s the price at which you vote.
          </p>
          <p style={{ margin: '0 0 1rem 0' }}>
            If sentiment moves your way by the market&apos;s end, you win. If not, you lose. For example, if{' '}
            <span style={yayPill}>YAY</span> is at 30% when you vote and it ends at 70% you win. If{' '}
            <span style={yayPill}>YAY</span> is at 80% when you pick it and it ends at 70% you lose.
          </p>

          <h4 style={sectionTitle}>How does the market end?</h4>
          <p style={{ margin: '0 0 1rem 0' }}>
            Markets end (voting closes to new entries and payouts become available) when max votes is reached or when
            the timer runs out.
          </p>

          <h4 style={sectionTitle}>Launch your market!</h4>
          <p style={{ margin: '0 0 1rem 0' }}>
            Launch a market to earn a share of platform fees of up to 5x your stake (proportional to the amount of
            staked ETH).
          </p>

          <h4 style={sectionTitle}>Risk</h4>
          <p style={{ margin: '0 0 0.5rem 0' }}>You can lose your full stake.</p>
          <p style={{ margin: 0 }}>
            Being in the majority doesn&apos;t guarantee profit — entry matters.
          </p>
        </div>
        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
          <p style={{ fontSize: '0.875rem', color: theme.textPrimary, marginBottom: '1rem', lineHeight: 1.5, fontWeight: 700 }}>
            We&apos;re only here for fun. You go it, baby!
          </p>
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: '0.5rem 1.25rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              border: 'none',
              borderRadius: '0.375rem',
              backgroundColor: theme.btnPrimaryBg ?? theme.primary,
              color: theme.btnPrimaryColor ?? 'white',
              cursor: 'pointer',
            }}
          >
            I got it, baby!
          </button>
        </div>
        <p style={{ fontSize: '0.8125rem', color: theme.textMuted, marginTop: '1rem', marginBottom: 0, lineHeight: 1.5, textAlign: 'center' }}>
          See{' '}
          <Link
            to="/docs"
            style={{
              color: theme.primary,
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '0.125rem 0',
              display: 'inline-block',
            }}
          >
            Documents
          </Link>
          {' '}
          for full math.
        </p>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
